import { goalChangeField } from 'shared/constants.json';

const MINUTE = 60 * 1000;
const GROUP_WINDOW_MINUTES = 10;

const fieldOrder = [
  goalChangeField.publishedAt,
  goalChangeField.icon,
  goalChangeField.title,
  goalChangeField.description,
  goalChangeField.goalCycle,
  goalChangeField.property,
  goalChangeField.progressMeasurement,
  goalChangeField.aggregationMethod,
  goalChangeField.start,
  goalChangeField.end,
  goalChangeField.threshold,
  goalChangeField.thresholdTargetArea,
  goalChangeField.metric,
  goalChangeField.unboundedProgress,
  goalChangeField.disableStatusAutoUpdate,
  goalChangeField.alignedItem,
  goalChangeField.parent,
];

const time = (change) => new Date(change.changedAt).getTime();

const fieldKey = (change) => (change.field === goalChangeField.property ? `${change.field}:${change.property?.uid}` : change.field);

const byFieldOrder = (a, b) => fieldOrder.indexOf(a.field) - fieldOrder.indexOf(b.field)
  || fieldKey(a).localeCompare(fieldKey(b));

export const groupWindowStart = (changedAt) => new Date(new Date(changedAt).getTime() - GROUP_WINDOW_MINUTES * MINUTE);

export const groupGoalChanges = (changes, windowMinutes = GROUP_WINDOW_MINUTES) => {
  const sorted = [...changes].sort((a, b) => time(b) - time(a));

  const groups = [];
  sorted.forEach((change) => {
    const current = groups[groups.length - 1];
    const sameActor = current !== undefined && current.actor?.uid === change.actor?.uid;
    if (sameActor && time(current.changes[current.changes.length - 1]) - time(change) <= windowMinutes * MINUTE) {
      current.changes.push(change);
      current.from = change.changedAt;
      return;
    }
    groups.push({ actor: change.actor, from: change.changedAt, to: change.changedAt, changes: [change] });
  });

  return groups.map((g) => ({ ...g, changes: [...g.changes].sort(byFieldOrder) }));
};

export const relationFields = [goalChangeField.alignedItem, goalChangeField.parent];

const removedFirst = (a, b) => Number(a.value !== null) - Number(b.value !== null);

const uids = (entities) => (entities ?? []).map((e) => e.uid).sort();

const isRoundTrip = (change) => change.field !== goalChangeField.property
  && JSON.stringify(change.previousValue ?? null) === JSON.stringify(change.value ?? null)
  && JSON.stringify(change.previousDescription ?? null) === JSON.stringify(change.description ?? null)
  && JSON.stringify(uids(change.previousGoalCycles)) === JSON.stringify(uids(change.goalCycles));

// Changes arrive newest first within a field: the merged change spans from the oldest's previous to the newest's value.
const mergeEdits = (edits) => {
  const oldest = edits[edits.length - 1];
  return {
    ...edits[0],
    previousValue: oldest.previousValue,
    previousDescription: oldest.previousDescription,
    previousPropertyValue: oldest.previousPropertyValue,
    previousGoalCycles: oldest.previousGoalCycles,
    previousMetric: oldest.previousMetric,
  };
};

// Expects a history entry's changes in field order. A field edited several times becomes one item spanning all edits;
// a relation field becomes one item listing the removed goals before the added ones.
export const goalChangeItems = (changes) => {
  const byKey = [];
  changes.forEach((change) => {
    const current = byKey[byKey.length - 1];
    if (current !== undefined && fieldKey(current[0]) === fieldKey(change)) {
      current.push(change);
      return;
    }
    byKey.push([change]);
  });

  return byKey.flatMap((edits) => {
    if (relationFields.includes(edits[0].field)) {
      return [[...edits].sort(removedFirst)];
    }
    if (edits.length === 1) {
      return [edits];
    }
    const merged = mergeEdits(edits);
    return isRoundTrip(merged) ? [] : [[merged]];
  });
};
